import { useEffect, useMemo, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Typography,
  Checkbox,
  Autocomplete,
  Chip,
  TextField,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import isEmpty from "lodash/isEmpty";

import { specialitySchema } from "../../yup";
import Input from "../../components/UserInput/Input";
import {
  useCreateSpecialityMutation,
  useGetClinicalDiagramsQuery,
} from "../../services/api";
import style from "./speciality.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import { getErrorMessage } from "../../utils/common";

type FormState = typeof specialitySchema.__outputType;
type ClinicalDiagramOption = {
  id: number;
  label: string;
  value: string;
};

/**
 * Add Speciality page for creating a new speciality.
 *
 * Notes:
 * - Uses clinical diagram options derived from the API list.
 * - Validation is enforced via `specialitySchema`.
 */
const AddSpeciality = () => {
  const [createSpeciality] = useCreateSpecialityMutation();
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const { data: clinicalDiagramData } = useGetClinicalDiagramsQuery({
    order_by_columns: "name",
    order: "Asc",
    is_detailed: true,
    search_key: "",
  });

  const clinicalDiagrams = useMemo<ClinicalDiagramOption[]>(
    () =>
      clinicalDiagramData?.items?.map((clinicalDiagram: ClinicalDiagram) => {
        return {
          id: clinicalDiagram?.id,
          label: clinicalDiagram?.name,
          value: clinicalDiagram?.name,
        };
      }) ?? [],
    [clinicalDiagramData]
  );
  const navigation = useNavigate();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(specialitySchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
  } = useForm<FormState>({
    resolver: yupResolver(specialitySchema),
    mode: "onChange",
  });

  /**
   * Submits the new speciality payload.
   *
   * Args:
   *   values (FormState): Form values for the new speciality.
   *
   * Returns:
   *   Promise<void>
   *
   * Raises:
   *   Error: Propagated when the create mutation fails.
   */
  const onSubmit = async (values: FormState) => {
    try {
      setIsLoading(true);
      const { name, clinical_diagram_ids } = values;
      const payload: AddSpecialityPayload = {
        name: name,
        clinical_diagram_ids:
          clinical_diagram_ids?.map((clinicalDiagram) => clinicalDiagram.id) ??
          [],
      };

      await createSpeciality(payload).unwrap();
      setIsLoading(false);
      navigationTimeoutRef.current = setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Please enter valid details"),
      });
      setIsLoading(false);
    }
  };

  return (
    <Box className={style.container}>
      <Typography className={style.heading}>Add Speciality</Typography>
      <Typography className={style.description}>
        Enter the below details to start adding a new speciality to Pegasus
        Cybersoft.
      </Typography>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Box className={style.flexTop}>
          <Box className={style.row}>
            <Input
              placeholder="Enter name"
              label="Speciality Name"
              required
              register={register}
              name="name"
              errors={errors}
            />
          </Box>
          <Box className={style.row} sx={{ flexDirection: "column" }}>
            <Typography className={style.inputLabel} sx={{ marginBottom: -2 }}>
              Clinical Diagram Mapping
            </Typography>
            <Controller
              control={control}
              name={"clinical_diagram_ids"}
              rules={{ required: false }}
              render={({ field: { onChange }, ...props }) => (
                <Autocomplete<ClinicalDiagramOption, true, true, false>
                  {...props}
                  sx={{
                    width: "100%",
                  }}
                  multiple
                  disablePortal
                  disableCloseOnSelect
                  id="tags-outlined"
                  options={clinicalDiagrams}
                  disableClearable={true}
                  onChange={(e, data) => onChange(data)}
                  getOptionLabel={(option: ClinicalDiagramOption) =>
                    option.label
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={
                        isEmpty(getValues("clinical_diagram_ids"))
                          ? "Select clinical diagrams"
                          : ""
                      }
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                      }}
                    />
                  )}
                  renderTags={(
                    value: readonly ClinicalDiagramOption[],
                    getTagProps
                  ) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option.label}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox style={{ marginRight: 8 }} checked={selected} />
                      {option.label}
                    </li>
                  )}
                />
              )}
            />
          </Box>
        </Box>
        {message && (
          <Box mt={2}>
            <LabelBox
              boxProps={{ height: 50, display: "flex", alignItems: "center" }}
              data={message}
              onClose={() => setMessage(null)}
            />
          </Box>
        )}
        <Box className={style.buttons}>
          <Button
            sx={{
              width: "200px",
              height: "55px",
              ":hover": { backgroundColor: "transparent" },
            }}
            variant="outlined"
            onClick={() => navigation(-1)}
            label={"Cancel"}
          />
          <Button
            sx={{
              width: "200px",
              height: "55px",
              ":hover": { backgroundColor: colors.primary },
            }}
            variant="contained"
            type="submit"
            label={"Save"}
            loading={isLoading}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AddSpeciality;
